import { Pause, PlayArrow } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Asset } from '../../types/asset.types';
import { isAudioUrl, isVideoUrl } from '../../utils/videoThumbnail';
import EnhancedLayerIcon from './EnhancedLayerIcon';
import VideoThumbnail from './VideoThumbnail';

interface AssetThumbnailProps {
  asset: Asset;
  width?: number;
  height?: number;
}

/**
 * Smart asset thumbnail component that handles both images and videos
 */
const AssetThumbnail: React.FC<AssetThumbnailProps> = ({
  asset,
  width = 40,
  height = 40,
}) => {
  // State to track image loading errors
  const [imageError, setImageError] = useState(false);

  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Reset error state when asset URL changes or album art changes (force refresh after edit)
  useEffect(() => {
    setImageError(false);
  }, [
    asset?.videoUrl,
    asset.metadata?.thumbnailUrl,
    asset.metadata?.fileUrl,
    asset.metadata?.audioUrl,
    asset.files?.[0]?.url,
    asset.id,
    asset.aiMetadata?.layerMetadata?.albumArt,
    (asset as any).songMetadata?.albumArt,
    (asset as any).albumArt,
  ]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle audio playback toggle
  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation

    const audioUrl =
      asset.metadata?.fileUrl ??
      asset.metadata?.audioUrl ??
      asset.files?.[0]?.url ??
      (asset as any).audioUrl;
    if (!audioRef.current && audioUrl) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener('ended', () => setIsPlaying(false));
      audioRef.current.addEventListener('error', () => {
        console.error('🎵 [AUDIO PLAYBACK] Failed to load audio:', audioUrl);
        setIsPlaying(false);
      });
    }

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(error => {
          console.error('🎵 [AUDIO PLAYBACK] Play failed:', error);
          setIsPlaying(false);
        });
        setIsPlaying(true);
      }
    }
  };

  const getLayerIcon = (layer: string) => {
    return (
      <EnhancedLayerIcon
        layer={layer}
        width={Math.min(width, height) * 0.8}
        height={Math.min(width, height) * 0.8}
        showLabel={false}
        showBadge={false}
      />
    );
  };

  const containerStyle = {
    width,
    height,
    borderRadius: 1,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'grey.100',
  };

  const effectiveVideoUrl =
    asset.metadata?.fileUrl ??
    asset.metadata?.videoUrl ??
    (asset as any).fileUrl ??
    (asset as any).videoUrl;
  const effectiveAudioUrl =
    asset.metadata?.fileUrl ??
    asset.metadata?.audioUrl ??
    asset.files?.[0]?.url ??
    (asset as any).audioUrl;

  // Handle video assets with VideoThumbnail component (M/W/C use videoUrl/fileUrl; other layers require isVideoUrl)
  if (
    effectiveVideoUrl &&
    asset.layer !== 'C' &&
    (asset.layer === 'M' ||
      asset.layer === 'W' ||
      isVideoUrl(effectiveVideoUrl))
  ) {
    return (
      <VideoThumbnail
        asset={{
          ...asset,
          metadata: { ...asset.metadata, videoUrl: effectiveVideoUrl },
        }}
        width={width}
        height={height}
        showFallbackIcon={true}
      />
    );
  } else if (
    effectiveAudioUrl &&
    (asset.layer === 'G' || isAudioUrl(effectiveAudioUrl))
  ) {
    // Use standardized album art URL resolution for consistency across all components
    const albumArtUrl: string = asset.metadata.thumbnailUrl;

    // Debug album art detection for all layers (layer-agnostic)
    if (albumArtUrl) {
      console.log(
        `🖼️ [ALBUM ART DEBUG] Layer ${asset.layer} Asset ${asset.id}:`,
        {
          foundUrl: albumArtUrl,
          layerMetadata: asset.aiMetadata?.layerMetadata?.albumArt,
          songMetadata: (asset as any).songMetadata?.albumArt,
          albumArt: (asset as any).albumArt,
          imageError,
        }
      );
    }

    if (albumArtUrl && !imageError) {
      return (
        <Box
          sx={{
            ...containerStyle,
            position: 'relative',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            key={`album-art-${asset.id}-${albumArtUrl}`} // Force React to recreate element when URL changes
            src={albumArtUrl}
            alt={`Album art for ${asset.friendlyName ?? asset.name ?? 'Asset'}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            onError={() => {
              // Fallback to layer icon if album art fails to load
              console.warn(
                '🖼️ [THUMBNAIL] Album art load failed:',
                albumArtUrl
              );
              setImageError(true);
            }}
            onLoad={() => {
              setImageError(false);
            }}
          />
          {/* Play/Pause Button Overlay */}
          {(isHovered || isPlaying) && (
            <IconButton
              onClick={handlePlayPause}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                width: Math.max(width * 0.4, 24),
                height: Math.max(height * 0.4, 24),
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  transform: 'translate(-50%, -50%) scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isPlaying ? (
                <Pause sx={{ fontSize: Math.max(width * 0.25, 16) }} />
              ) : (
                <PlayArrow sx={{ fontSize: Math.max(width * 0.25, 16) }} />
              )}
            </IconButton>
          )}
        </Box>
      );
    }

    // Fallback to layer icon for audio assets without album art or when album art fails
    // Still show play button so user can hear the audio
    return (
      <Box
        sx={{
          ...containerStyle,
          position: 'relative',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {getLayerIcon(asset.layer)}
        {/* Play/Pause Button Overlay - same as when album art is present */}
        {(isHovered || isPlaying) && (
          <IconButton
            onClick={handlePlayPause}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              width: Math.max(width * 0.4, 24),
              height: Math.max(height * 0.4, 24),
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isPlaying ? (
              <Pause sx={{ fontSize: Math.max(width * 0.25, 16) }} />
            ) : (
              <PlayArrow sx={{ fontSize: Math.max(width * 0.25, 16) }} />
            )}
          </IconButton>
        )}
      </Box>
    );
  } else {
    return (
      <Box sx={containerStyle}>
        {imageError ? (
          // Show layer icon fallback when image fails to load
          getLayerIcon(asset.layer)
        ) : (
          <img
            src={asset.imageUrl ?? asset.thumbnailUrl}
            alt={`${asset.friendlyName ?? asset.name ?? 'Asset'} thumbnail`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            onError={() => {
              // Set error state to trigger layer icon fallback
              setImageError(true);
            }}
            onLoad={() => {
              // Reset error state when image loads successfully
              setImageError(false);
            }}
          />
        )}
      </Box>
    );
  }
};

export default AssetThumbnail;
